/**
 * @fileoverview Implementación concreta del repositorio de autenticación con Firebase
 * Adaptador para el puerto IAuthRepository utilizando Firebase Auth
 */

import { 
  getAuth, 
  signInWithCustomToken, 
  signOut as firebaseSignOut,
  onAuthStateChanged, 
  getIdTokenResult
} from 'firebase/auth';
import IAuthRepository from '../../domain/ports/IAuthRepository';
import TokenInfo from '../../domain/value-objects/TokenInfo';
import User from '../../domain/entities/User';

/**
 * @class FirebaseAuthRepository
 * @implements {IAuthRepository}
 * @description Adaptador de Firebase Auth para el puerto IAuthRepository
 * Implementa el patrón Adapter para conectar Firebase Auth con el dominio
 */
class FirebaseAuthRepository extends IAuthRepository {
  /**
   * @constructor
   * @param {Object} logger - Sistema de logging para rastreo de eventos
   */
  constructor(logger) {
    super();
    this.auth = getAuth();
    this.logger = logger || console;
    this.logger.info('[FirebaseAuthRepository] Inicializado');
  }

  /**
   * @inheritdoc
   */
  async signInWithCustomToken(token) {
    this.logger.info('[FirebaseAuthRepository] Iniciando sesión con token personalizado');
    
    try {
      const userCredential = await signInWithCustomToken(this.auth, token);
      const tokenResult = await this.getIdTokenResult();
      
      this.logger.info('[FirebaseAuthRepository] Sesión iniciada exitosamente');
      
      // Calculamos la fecha de expiración a partir de la fecha de emisión y la expiración en segundos
      const expiresAt = new Date(tokenResult.expirationTime);
      
      // Crear TokenInfo para abstracto de Firebase Auth
      const tokenInfo = new TokenInfo({
        token: tokenResult.token,
        expiresAt,
        type: 'Bearer'
      });
      
      // Mapear UserCredential a nuestro domain entity User
      const userData = {
        uid: userCredential.user.uid,
        storeId: userCredential.user.uid, // En este caso son iguales
        email: userCredential.user.email,
        role: tokenResult.claims?.role || 'user',
        metadata: {
          lastLoginAt: new Date(),
          ...tokenResult.claims
        }
      };
      
      if (tokenResult.claims?.storeName) {
        userData.storeName = tokenResult.claims.storeName;
      }
      
      return {
        user: new User(userData),
        tokenInfo
      };
    } catch (error) {
      this.logger.error('[FirebaseAuthRepository] Error al iniciar sesión:', error);
      throw new Error(`Error de autenticación: ${error.message}`);
    }
  }

  /**
   * @inheritdoc
   */
  async signOut() {
    this.logger.info('[FirebaseAuthRepository] Cerrando sesión');
    try {
      await firebaseSignOut(this.auth);
      this.logger.info('[FirebaseAuthRepository] Sesión cerrada exitosamente');
    } catch (error) {
      this.logger.error('[FirebaseAuthRepository] Error al cerrar sesión:', error);
      throw new Error(`Error al cerrar sesión: ${error.message}`);
    }
  }

  /**
   * @inheritdoc
   */
  async getAuthProviderUrl(provider) {
    // En este caso delegamos al backend la generación de la URL
    this.logger.info(`[FirebaseAuthRepository] Solicitando URL para proveedor: ${provider}`);
    
    // Esta es una implementación simplificada - en producción usaríamos fetch/axios
    // para solicitar al backend la URL de OAuth
    const baseUrl = process.env.REACT_APP_API_BASE_URL || 'https://nubelytics.com/api';
    return `${baseUrl}/auth/login?provider=${provider}`;
  }

  /**
   * @inheritdoc
   */
  async getIdToken() {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('Usuario no autenticado');
      }
      
      // Forzar refresh si el token expira en menos de 5 minutos
      const forceRefresh = await this.isTokenExpired(5);
      const token = await user.getIdToken(forceRefresh);
      
      // Guardar datos para diagnóstico
      try {
        localStorage.setItem('last_token_time', new Date().toISOString());
        
        // Guardar primeros y últimos 10 caracteres del token para diagnóstico
        if (token?.length > 30) {
          const tokenPreview = `${token.substring(0, 10)}...${token.substring(token.length - 10)}`;
          localStorage.setItem('last_token_preview', tokenPreview);
        }
      } catch (e) {
        this.logger.warn('[FirebaseAuthRepository] No se pudieron guardar datos de diagnóstico', e);
      }
      
      this.logger.info(`[FirebaseAuthRepository] Token obtenido: ${token ? 'OK' : 'ERROR'} (${token?.length || 0} caracteres)`);
      
      return token;
    } catch (error) {
      this.logger.error('[FirebaseAuthRepository] Error al obtener token:', error);
      throw error;
    }
  }

  /**
   * Obtiene token especialmente formateado para uso con la API
   * @param {boolean} [forceRefresh=false] - Forzar renovación del token
   * @returns {Promise<string>} Token de API en formato esperado por el backend
   */
  async getApiCompatibleToken(forceRefresh = false) {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('Usuario no autenticado');
      }
      
      // Obtener token normal de Firebase
      const firebaseToken = await user.getIdToken(forceRefresh);
      
      // Intentar obtener el token correcto para la API desde un endpoint de conversión
      // Nota: Este es un enfoque temporal hasta corregir la validación en el backend
      try {
        // Intentar obtener token para API del backend - si falla, usamos el token de Firebase
        const apiBaseUrl = process.env.REACT_APP_API_BASE_URL || 'https://nubelytics.com/api';
        const response = await fetch(`${apiBaseUrl}/auth/convert-token`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${firebaseToken}`
          },
          body: JSON.stringify({
            uid: user.uid,
            provider: 'firebase'
          })
        });
        
        // Si el endpoint de conversión falla, usamos el token original
        if (response.ok) {
          const data = await response.json();
          if (data.token) {
            this.logger.info('[FirebaseAuthRepository] Token convertido para API correctamente');
            localStorage.setItem('api_token_source', 'converter');
            return data.token;
          }
        }
      } catch (converterError) {
        this.logger.warn('[FirebaseAuthRepository] Error al convertir token para API, usando token original:', converterError);
      }
      
      // Plan B: Usar el token original de Firebase con un formato alternativo para el backend
      // Algunos backends esperan "tiendanube <token>" en lugar de "Bearer <token>"
      this.logger.info('[FirebaseAuthRepository] Usando formato alternativo de token para API');
      localStorage.setItem('api_token_source', 'firebase_direct');
      
      // Retornamos el token sin la palabra Bearer, para que apiClient lo formatee adecuadamente
      return firebaseToken;
    } catch (error) {
      this.logger.error('[FirebaseAuthRepository] Error al obtener token para API:', error);
      throw error;
    }
  }
  
  /**
   * Obtiene el resultado completo del ID token con decodificación de claims
   * @returns {Promise<firebase.auth.IdTokenResult>} Resultado completo del token
   * @throws {Error} Si no hay usuario autenticado
   * @private
   */
  async getIdTokenResult() {
    const user = this.auth.currentUser;
    if (!user) {
      throw new Error('Usuario no autenticado');
    }
    
    return user.getIdTokenResult();
  }

  /**
   * @inheritdoc
   */
  async isAuthenticated() {
    return Boolean(this.auth.currentUser);
  }

  /**
   * @inheritdoc
   */
  onAuthStateChanged(callback) {
    this.logger.info('[FirebaseAuthRepository] Configurando observer onAuthStateChanged');
    
    const unsubscribe = onAuthStateChanged(this.auth, async (user) => {
      try {
        if (!user) {
          this.logger.info('[FirebaseAuthRepository] Usuario no autenticado');
          callback(null);
          return;
        }
        
        this.logger.info(`[FirebaseAuthRepository] Usuario autenticado: ${user.uid}`);
        
        // Obtener claims y detalles adicionales
        const tokenResult = await user.getIdTokenResult();
        
        // Mapear a nuestro domain entity User
        const userData = {
          uid: user.uid,
          storeId: user.uid, // En el caso típico son iguales
          email: user.email,
          role: tokenResult.claims?.role || 'user',
          metadata: {
            lastLoginAt: new Date(),
            ...tokenResult.claims
          }
        };
        
        if (tokenResult.claims?.storeName) {
          userData.storeName = tokenResult.claims.storeName;
        }
        
        // Crear entidad de dominio y notificar
        const domainUser = new User(userData);
        callback(domainUser);
      } catch (error) {
        this.logger.error('[FirebaseAuthRepository] Error en observer:', error);
        callback(null, error);
      }
    });
    
    return unsubscribe;
  }

  /**
   * @inheritdoc
   */
  async refreshToken() {
    try {
      const user = this.auth.currentUser;
      if (!user) {
        throw new Error('Usuario no autenticado');
      }
      
      // Firebase getIdToken(true) fuerza un refresh del token
      const token = await user.getIdToken(true);
      return token;
    } catch (error) {
      this.logger.error('[FirebaseAuthRepository] Error al refrescar token:', error);
      throw error;
    }
  }

  /**
   * @inheritdoc
   */
  async isTokenExpired(threshold = 5) {
    try {
      const user = this.auth.currentUser;
      if (!user) return true;
      
      const tokenResult = await user.getIdTokenResult();
      const expirationTime = new Date(tokenResult.expirationTime).getTime();
      const now = Date.now();
      const thresholdMs = threshold * 60 * 1000;
      
      return (expirationTime - now) < thresholdMs;
    } catch (error) {
      this.logger.error('[FirebaseAuthRepository] Error al verificar expiración:', error);
      return true; // Por seguridad, asumir que está expirado si hay error
    }
  }
}

export default FirebaseAuthRepository;
